// @givmo/app-admin — the donor disclosure for a match payment (CAM-X4 web). ★ MONEY-MOVING ★
//
// Approving a match is the COMPANY giving, so before it pays it is shown the company-payer body for
// the match's target on the chosen rail (CAM-X3, D-CAM-26 ruling E), and the approve request echoes
// what it was shown (`shown_disclaimer_version_id` / `shown_disclaimer_content_hash`).
//
// WHY A MATCH-KEYED READ, NOT `GET /disclaimers/resolve`. The body is chosen by the match's TARGET
// (charity → DONATE_CONFIRMATION; ETF or collection → CAUSE_ETF; fundraiser → DONATE_CONFIRMATION by
// fundraiser id), and the admin wire cannot always name it: the dashboard strips a collection's
// preview (CC-BE16), so a collection match reaches this app with no collection id. The backend
// derives the target from its own records and serves the body its gate will expect, so what this
// dialog shows and what the approve checks come from one derivation:
// `GET /admin/company/matches/{match_id}/disclosure?funding=card|wallet` (any match id of a grouped
// request — its legs share one target). Same `DisclaimerResolveOut` shape and ETag as the resolve.
//
// The route landed after the committed OpenAPI snapshot, so it is called through the configured
// client (`agent_docs/api-client.md` § "A whole ROUTE the snapshot lacks"); graduate to the
// generated op when a routine regen absorbs it.
//
// The decode FAILS CLOSED: its output lands in a record (the echo), so a body without an id, a hash
// or a line is no body at all, and the dialog blocks rather than echo a guess.

import { givmoClient } from '@givmo/api-client';
import { splitPlaintext, type DisclosureFunding, type ResolvedDisclosure } from '@givmo/disclosure';

/** The match-keyed disclosure read's path (the generated op's shape, hand-written). */
export function matchDisclosureUrl(matchId: string): string {
  return `/admin/company/matches/${encodeURIComponent(matchId)}/disclosure`;
}

function nonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

/** Decode a `DisclaimerResolveOut` body; `null` unless it carries an id, a hash and a line. */
export function decodeMatchDisclosure(body: unknown): ResolvedDisclosure | null {
  if (typeof body !== 'object' || body === null) return null;
  const record = body as Record<string, unknown>;
  const versionId = nonEmptyString(record.id);
  const contentHash = nonEmptyString(record.content_hash);
  const plaintext = nonEmptyString(record.content_plaintext);
  if (versionId === null || contentHash === null || plaintext === null) return null;
  const lines = splitPlaintext(plaintext);
  if (lines.length === 0) return null;
  return { lines, contentHash, versionId, isFloor: false };
}

/**
 * Read the company-payer body for one match on a rail. `null` on a 404 (not this company's match,
 * or nothing resolves), any other error, a transport failure or an unreadable body — never throws.
 */
export async function fetchMatchDisclosure(
  matchId: string,
  rail: DisclosureFunding,
): Promise<ResolvedDisclosure | null> {
  try {
    const { data, error } = await givmoClient.get({
      url: matchDisclosureUrl(matchId),
      query: { funding: rail },
    });
    if (error !== undefined && error !== null) return null;
    return decodeMatchDisclosure(data);
  } catch {
    return null;
  }
}

/** One approvable unit's resolved body, in the order the dialog charges them. */
export interface MatchDisclosureLeg {
  /** The unit's match ids (the key its approve request is built from). */
  matchIds: readonly string[];
  /** Who the unit pays — a charity, ETF or fundraiser name — for labelling several bodies. */
  name: string | null;
  disclosure: ResolvedDisclosure;
}

/**
 * Resolve every unit's body on a rail, or `null` when ANY of them cannot be shown: a batch never
 * pays some matches with their text and others without.
 */
export async function fetchMatchDisclosures(
  units: readonly { matchIds: readonly string[]; name: string | null }[],
  rail: DisclosureFunding,
): Promise<MatchDisclosureLeg[] | null> {
  const resolved = await Promise.all(
    units.map(async (unit) => {
      const first = unit.matchIds[0];
      if (first === undefined) return null;
      const disclosure = await fetchMatchDisclosure(first, rail);
      return disclosure === null ? null : { matchIds: unit.matchIds, name: unit.name, disclosure };
    }),
  );
  if (resolved.length === 0 || resolved.some((leg) => leg === null)) return null;
  return resolved as MatchDisclosureLeg[];
}

/** The body a unit (identified by its match ids) was shown, or `null` when none was resolved. */
export function disclosureForMatches(
  legs: readonly MatchDisclosureLeg[],
  matchIds: readonly string[],
): ResolvedDisclosure | null {
  const key = [...matchIds].sort().join('\x00');
  const leg = legs.find((candidate) => [...candidate.matchIds].sort().join('\x00') === key);
  return leg?.disclosure ?? null;
}

/**
 * The bodies to show, one per distinct document in first-appearance order. A single body carries
 * no label (it is the dialog's only one); several are each labelled with the recipients they
 * apply to, so an approver can tell which terms govern which match.
 */
export function matchDisclosureSections(
  legs: readonly MatchDisclosureLeg[],
): { key: string; label: string | null; lines: readonly string[] }[] {
  const order: string[] = [];
  const byHash = new Map<string, { lines: readonly string[]; names: string[] }>();
  for (const leg of legs) {
    const hash = leg.disclosure.contentHash;
    let entry = byHash.get(hash);
    if (entry === undefined) {
      entry = { lines: leg.disclosure.lines, names: [] };
      byHash.set(hash, entry);
      order.push(hash);
    }
    if (leg.name !== null && !entry.names.includes(leg.name)) entry.names.push(leg.name);
  }
  const labelled = order.length > 1;
  return order.map((hash) => {
    const entry = byHash.get(hash)!;
    return {
      key: hash,
      label: labelled && entry.names.length > 0 ? `For matches to ${entry.names.join(', ')}` : null,
      lines: entry.lines,
    };
  });
}
